// Tiny viewport-aware helpers. Centralized so every "is this mobile?"
// check uses the same breakpoint (768 px — matches the SCSS media
// queries throughout the app). Synchronous reads from
// matchMedia, no listener — call sites can re-evaluate at any moment
// (e.g. when adding a marker), but a viewport resize doesn't notify.
// Good enough for one-shot scaling decisions; if a future feature
// needs reactive viewport tracking we can layer that on with a store.

import type { MarkerStyle } from '@atlasguess/engine';

/** True when the viewport matches the SCSS-side `(max-width: 768px)`. */
export function isMobileViewport(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia('(max-width: 768px)').matches;
}

/**
 * Bump a marker style's `size` by ~30% on mobile viewports, return it
 * unchanged elsewhere. Used by country-quiz / country-practice modes
 * for the small-country dots — the 0.015 world-unit default is hard
 * to tap on phone screens, so we trade a touch of visual weight on
 * desktop-vs-mobile parity for a reliably hittable target on touch.
 *
 * Returns a new object on mobile so the frozen markerStyles presets
 * stay untouched and other call sites with the same preset get the
 * unmodified original.
 */
const MOBILE_PIN_SCALE = 1.3;

export function mobileBumpedMarker(style: MarkerStyle): MarkerStyle {
  if (!isMobileViewport()) return style;
  return { ...style, size: (style.size ?? 0.015) * MOBILE_PIN_SCALE };
}
